import { Children, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import { Tab } from './Tab';

/**
 * TabList Component
 *
 * Renders a horizontal list of tabs with flexible spacing and styling.
 * Works as part of the Tabs component system to display clickable tab headers.
 *
 * Features:
 * - Flexible layout with configurable spacing
 * - Support for border and background variants
 * - Automatic active state management
 * - Custom styling support
 *
 * @example
 * ```tsx
 * <TabList setActiveTab={setActiveTab} activeTab={0}>
 *   <Tab title="Tab 1" />
 *   <Tab title="Tab 2" />
 * </TabList>
 * ```
 */

interface ITabListProps {
  /** The tab items to render */
  children: ReactNode;
  /** Callback fired when a tab is clicked. Automatically provided by Tabs parent component when used as child. */
  setActiveTab?: (index: number) => void;
  /** The index of the active tab. Automatically provided by Tabs parent component when used as child. */
  activeTab?: number;
  /** Custom styles applied to the tab list container */
  style?: CSSProperties;
  /** The visual style used to highlight the active tab */
  variant?: 'border' | 'background';
}

export const TabList = ({
  children,
  setActiveTab,
  activeTab = 0,
  style = {},
  variant = 'border',
}: ITabListProps) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'row',
      marginBottom: 20,
      ...style,
    }}
  >
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-around',
        marginBottom: variant === 'background' ? 0 : 20,
        gap: variant === 'background' ? 10 : 40,
        ...style,
      }}
    >
      {Children.map(children, (child, index) => {
        const element = child as ReactElement<any>;
        if (
          element?.type &&
          typeof element.type === 'function' &&
          (element.type as any).displayName === 'Tab'
        ) {
          return (
            <Tab
              variant={variant}
              isActive={activeTab === index}
              setActiveTab={setActiveTab ? () => setActiveTab(index) : undefined}
              {...element.props}
            />
          );
        }
        return null;
      })}
    </div>
  </div>
);

TabList.displayName = 'TabList';
